import {
    Film,
    FolderOpen,
    Grid3X3,
    Image as ImageIcon,
    Loader2,
    Search,
    Upload,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { trpc } from '@/lib/api/trpc'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

import { ImageUpload } from '@/components/form/image-upload'

type MediaType = 'image' | 'video' | 'all'

interface MediaAsset {
    id: string
    url: string
    filename: string
    mimeType: string
    fileSize?: number | null
    width?: number | null
    height?: number | null
    alt?: string | null
    createdAt?: Date | null
}

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSelect: (url: string, asset?: MediaAsset) => void
    accept?: MediaType
    title?: string
}

/**
 * Programmatic picker on top of the same `mediaLibrary.list` tRPC as Studio +
 * CRM. Opened imperatively via the <ImagePickerProvider /> context.
 *
 * New uploads go through the existing `images.upload` flow (via <ImageUpload/>)
 * and are registered into `media_assets` so they show up in the grid next time
 * and are visible from Studio too.
 */
export function MediaLibraryDialog({
    open,
    onOpenChange,
    onSelect,
    accept = 'image',
    title = 'Media library',
}: Props) {
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [typeFilter, setTypeFilter] = useState<MediaType>(accept)
    const [page, setPage] = useState(0)
    const [tab, setTab] = useState<'browse' | 'upload'>('browse')
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const { t } = useTranslation('emails')

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
        searchTimeoutRef.current = setTimeout(() => setDebouncedSearch(search), 250)
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
        }
    }, [search])

    // Reset ephemeral UI state on close
    useEffect(() => {
        if (!open) {
            setSearch('')
            setDebouncedSearch('')
            setPage(0)
            setTab('browse')
        }
    }, [open])

    const { data, isLoading } = trpc.mediaLibrary.list.useQuery(
        {
            page,
            perPage: 30,
            search: debouncedSearch || undefined,
            type: typeFilter,
        },
        { enabled: open && tab === 'browse' },
    )

    const registerMutation = trpc.mediaLibrary.register.useMutation()
    const utils = trpc.useUtils()

    const assets: MediaAsset[] = data?.assets ?? []
    const hasNext = data?.pagination?.hasNext ?? false
    const total = data?.pagination?.total ?? 0

    const handleUploaded = async (url: string) => {
        const filename = decodeURIComponent(url.split('/').pop() || 'image')
        try {
            const registered = await registerMutation.mutateAsync({
                url,
                filename,
                mimeType: guessMime(filename),
            })
            utils.mediaLibrary.list.invalidate()
            onSelect(registered.url, registered as unknown as MediaAsset)
        } catch {
            // Fall back: still return the raw URL so the merchant isn't blocked
            onSelect(url)
        }
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[80vh] max-w-3xl flex-col overflow-hidden">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-sm">
                        <FolderOpen className="h-4 w-4" />
                        {title}
                        {total > 0 && tab === 'browse' && (
                            <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                                {total}
                            </span>
                        )}
                    </DialogTitle>
                </DialogHeader>

                {/* Tabs */}
                <div className="flex items-center gap-1 border-b pb-2">
                    <TabButton
                        active={tab === 'browse'}
                        onClick={() => setTab('browse')}
                        icon={Grid3X3}
                    >
                        Browse
                    </TabButton>
                    <TabButton
                        active={tab === 'upload'}
                        onClick={() => setTab('upload')}
                        icon={Upload}
                    >
                        Upload
                    </TabButton>
                </div>

                {tab === 'browse' && (
                    <>
                        <div className="flex items-center gap-2 pb-3">
                            <div className="relative flex-1">
                                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value)
                                        setPage(0)
                                    }}
                                    placeholder={t('builder.mediaLibrary.searchFiles')}
                                    className="h-8 pl-8 text-xs"
                                />
                            </div>
                            {accept === 'all' && (
                                <div className="flex items-center gap-0.5 rounded-md bg-muted p-0.5">
                                    {(
                                        [
                                            ['all', Grid3X3, 'All'],
                                            ['image', ImageIcon, 'Images'],
                                            ['video', Film, 'Videos'],
                                        ] as const
                                    ).map(([val, Icon, label]) => (
                                        <button
                                            key={val}
                                            type="button"
                                            onClick={() => {
                                                setTypeFilter(val as MediaType)
                                                setPage(0)
                                            }}
                                            className={cn(
                                                'flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors',
                                                typeFilter === val
                                                    ? 'bg-background text-foreground shadow-sm'
                                                    : 'text-muted-foreground hover:text-foreground',
                                            )}
                                        >
                                            <Icon className="h-3 w-3" />
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto">
                            {isLoading && assets.length === 0 ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                </div>
                            ) : assets.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12 text-center">
                                    <FolderOpen className="mb-3 h-10 w-10 text-muted-foreground/30" />
                                    <p className="text-sm font-medium text-muted-foreground">
                                        No media yet
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setTab('upload')}
                                        className="mt-2 text-xs text-primary underline"
                                    >
                                        Upload your first image
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-5 gap-2">
                                        {assets.map((asset) => (
                                            <button
                                                key={asset.id}
                                                type="button"
                                                onClick={() => {
                                                    onSelect(asset.url, asset)
                                                    onOpenChange(false)
                                                }}
                                                className="group relative aspect-square overflow-hidden rounded-md border-2 border-transparent transition-all hover:border-primary/50"
                                            >
                                                {asset.mimeType.startsWith('image/') ? (
                                                    <img
                                                        src={asset.url}
                                                        alt={asset.alt || asset.filename}
                                                        loading="lazy"
                                                        className="h-full w-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="flex h-full w-full flex-col items-center justify-center bg-muted">
                                                        <Film className="mb-1 h-6 w-6 text-muted-foreground/40" />
                                                        <span className="max-w-full truncate px-2 text-[9px] text-muted-foreground">
                                                            {asset.filename}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="absolute inset-0 flex items-end bg-black/0 opacity-0 transition-all group-hover:bg-black/30 group-hover:opacity-100">
                                                    <div className="w-full bg-gradient-to-t from-black/60 to-transparent p-1.5">
                                                        <p className="truncate text-[10px] font-medium text-white">
                                                            {asset.filename}
                                                        </p>
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                    {hasNext && (
                                        <div className="mt-3 flex justify-center pb-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setPage((p) => p + 1)}
                                                disabled={isLoading}
                                                className="text-xs"
                                            >
                                                {isLoading && (
                                                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                )}
                                                Load more
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}

                {tab === 'upload' && (
                    <div className="flex flex-1 flex-col items-center justify-center p-6">
                        <div className="w-full max-w-md">
                            <ImageUpload
                                url={undefined}
                                onImageAvailable={(url) => handleUploaded(url)}
                            />
                            <p className="mt-3 text-center text-xs text-muted-foreground">
                                Uploaded images appear in your media library and in
                                the studio builder.
                            </p>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}

function TabButton(props: {
    active: boolean
    onClick: () => void
    icon: React.ComponentType<{ className?: string }>
    children: React.ReactNode
}) {
    const { active, onClick, icon: Icon, children } = props
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                active
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
            )}
        >
            <Icon className="h-3.5 w-3.5" />
            {children}
        </button>
    )
}

function guessMime(filename: string): string {
    const ext = filename.toLowerCase().split('.').pop() || ''
    const map: Record<string, string> = {
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        gif: 'image/gif',
        webp: 'image/webp',
        svg: 'image/svg+xml',
        mp4: 'video/mp4',
        webm: 'video/webm',
    }
    return map[ext] || 'application/octet-stream'
}
